import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Recycle, ChevronDown, ChevronUp, Lock, HelpCircle, X, ChevronLeft, ChevronRight, MessageCircle, FileEdit, Bell } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { GibletChatDialogEnhanced } from "@/components/chat/GibletChatDialogEnhanced";
import gibletOwlIcon from "@/assets/giblet-owl-icon.png";
import ispaiDemoQr from "@/assets/ispai-demo-qr.png";
import spaiCycle from "@/assets/spai-cycle.png";
import consumptionWorkflow from "@/assets/consumption-workflow.png";
import ispaiLogo from "@/assets/ispai-logo.png";
import Navbar from "@/components/Navbar";
import IspaiHeader from "@/components/ispai/IspaiHeader";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
const Intro = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [comparisonValue, setComparisonValue] = useState([0]);
  const [isOpen, setIsOpen] = useState(false);
  const [isInvitationOpen, setIsInvitationOpen] = useState(false);
  const [isContributeOpen, setIsContributeOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isPayOpen, setIsPayOpen] = useState(false);

  // Registration dialog state
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState("");

  // Consumer form fields
  const [companyName, setCompanyName] = useState("");
  const [individualName, setIndividualName] = useState("");
  const [individualRole, setIndividualRole] = useState("");
  const [industry, setIndustry] = useState("");
  const [email, setEmail] = useState("");
  const [scheduleBriefing, setScheduleBriefing] = useState(false);
  const [authorizeWaitlist, setAuthorizeWaitlist] = useState(false);
  const [includeNameOnWaitlist, setIncludeNameOnWaitlist] = useState(false);
  const [inviteFriend, setInviteFriend] = useState(false);
  const [friendName, setFriendName] = useState("");
  const [friendEmail, setFriendEmail] = useState("");
  const [comments, setComments] = useState("");

  // Creator form fields
  const [creatorName, setCreatorName] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");
  const [creatorExpertise, setCreatorExpertise] = useState("");
  const [creatorExperience, setCreatorExperience] = useState("");
  const [creatorIncludeNameOnWaitlist, setCreatorIncludeNameOnWaitlist] = useState(false);
  const [creatorInviteFriend, setCreatorInviteFriend] = useState(false);
  const [creatorFriendName, setCreatorFriendName] = useState("");
  const [creatorFriendEmail, setCreatorFriendEmail] = useState("");
  const [creatorComments, setCreatorComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [gibletChatOpen, setGibletChatOpen] = useState(false);

  // Refs for each section to enable smooth scrolling
  const introducingRef = useRef<HTMLDivElement>(null);
  const interactiveRef = useRef<HTMLDivElement>(null);
  const differenceRef = useRef<HTMLDivElement>(null);
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleUserTypeSelection = (userType: string) => {
    setSelectedUserType(userType);
    setRegisterDialogOpen(true);
  };
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call with timeout
    setTimeout(() => {
      setIsSubmitting(false);
      setRegisterDialogOpen(false);
      setShowCreateProfile(true);

      // TODO: When Supabase is connected, log friend invitations here
      if (selectedUserType === "Consumer" && inviteFriend && friendName && friendEmail) {
        console.log("Consumer friend invitation to log:", {
          friendName,
          friendEmail,
          inviterName: individualName
        });
        toast.info(`Invitation will be sent to ${friendName} at ${friendEmail}`);
      }
      if (selectedUserType === "Creator" && creatorInviteFriend && creatorFriendName && creatorFriendEmail) {
        console.log("Creator friend invitation to log:", {
          friendName: creatorFriendName,
          friendEmail: creatorFriendEmail,
          inviterName: creatorName
        });
        toast.info(`Invitation will be sent to ${creatorFriendName} at ${creatorFriendEmail}`);
      }

      // Reset all form fields
      setCompanyName("");
      setIndividualName("");
      setIndividualRole("");
      setIndustry("");
      setEmail("");
      setScheduleBriefing(false);
      setAuthorizeWaitlist(false);
      setIncludeNameOnWaitlist(false);
      setInviteFriend(false);
      setFriendName("");
      setFriendEmail("");
      setComments("");
      setCreatorName("");
      setCreatorEmail("");
      setCreatorExpertise("");
      setCreatorExperience("");
      setCreatorIncludeNameOnWaitlist(false);
      setCreatorInviteFriend(false);
      setCreatorFriendName("");
      setCreatorFriendEmail("");
      setCreatorComments("");

      // Show success message
      if (selectedUserType === "Consumer") {
        toast.success("Thank you for joining our Consumer waitlist! We'll be in touch soon.");
        if (scheduleBriefing) {
          toast.info("We'll contact you to schedule your briefing.");
        }
      } else {
        toast.success("Thank you for joining our Creator waitlist! We'll be in touch soon.");
      }
    }, 1500);
  };
  const handleCreateProfile = () => {
    setShowCreateProfile(false);
    // Navigate to the profile creation page
    navigate("/create-profile");
  };

  const nextScreen = () => {
    if (currentScreen < 6) {
      setCurrentScreen(currentScreen + 1);
    }
  };

  const previousScreen = () => {
    if (currentScreen > 1) {
      setCurrentScreen(currentScreen - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        nextScreen();
      } else if (e.key === "ArrowLeft") {
        previousScreen();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentScreen]);

  // Track intro screen views for invitation analytics
  useEffect(() => {
    const trackScreenView = async () => {
      const invitationData = sessionStorage.getItem("invitation_data");
      const fromInvitation = sessionStorage.getItem("from_invitation_explore");
      
      if (invitationData && fromInvitation) {
        try {
          const invitation = JSON.parse(invitationData);
          await supabase.functions.invoke("track-invitation-response", {
            body: {
              tracking_token: invitation.tracking_token,
              action_type: "intro_screen_viewed",
              metadata: { screen: currentScreen },
            },
          });
        } catch (error) {
          console.error("Error tracking intro screen view:", error);
        }
      }
    };

    trackScreenView();
  }, [currentScreen]);

  // Animation variants
  const screenVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };
  return <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Screen Indicators */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        {[1, 2, 3, 4, 5, 6].map((screen) => (
          <button
            key={screen}
            onClick={() => setCurrentScreen(screen)}
            className={`w-3 h-3 rounded-full transition-all ${
              currentScreen === screen ? "bg-primary w-6" : "bg-gray-300"
            }`}
            aria-label={`Go to screen ${screen}`}
          />
        ))}
      </div>

      {/* Navigation Controls */}
      {currentScreen > 1 && (
        <button
          onClick={previousScreen}
          className="fixed left-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          aria-label="Previous screen"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      
      {currentScreen < 6 && (
        <button
          onClick={nextScreen}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-50 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          aria-label="Next screen"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      <AnimatePresence mode="wait">
        {/* Screen 1: Welcome */}
        {currentScreen === 1 && (
          <motion.div
            key="screen1"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col"
            onClick={nextScreen}
          >
            <div className="p-3 md:p-6 bg-white shadow-sm">
              <IspaiHeader />
            </div>
            
            <div className="bg-gray-50 border-b border-gray-200 p-4 md:p-6 flex-1 flex flex-col justify-center">
              <div className="max-w-4xl mx-auto">
                <p className="text-xl text-gray-700 text-center leading-relaxed">
                  Welcome to our prototype of the iSPAI Platform. You are among the first to get a glimpse at what we're building, and we're looking forward to your open and candid feedback. Thanks in advance for your time. Together we will define a new role for humans in an AI-driven future.
                </p>
                <p className="text-xl text-gray-900 text-center mt-3 font-semibold">
                  The iSPAI Team
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Screen 2: Value Proposition */}
        {currentScreen === 2 && (
          <motion.div
            key="screen2"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col p-6 md:p-12 bg-white"
            onClick={(e) => {
              // Don't advance if clicking on interactive elements
              if ((e.target as HTMLElement).closest('button')) return;
              nextScreen();
            }}
          >
          <div className="flex justify-center mb-6">
            <img src={ispaiLogo} alt="iSPAI Logo" className="h-24 w-auto" />
            </div>
            <div className="flex-1 flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-1/2 mb-8 md:mb-0 md:pr-12 md:pl-16">
                <h1 className="text-3xl font-bold mb-4 text-black md:text-2xl">What if your team never lost 
a great idea or lessons 
learned again?</h1>
                <p className="text-base mb-4 text-gray-700">iSPAI helps teams 
capture and reuse hard-won 
knowledge — using AI, not just 
memory.</p>
                <p className="text-base mb-6 text-gray-700">No more lost insights. No more 
repeated mistakes. Just connected
knowledge that works.</p>
              </div>
              <div className="md:w-1/2 flex justify-center md:border-l md:border-gray-200 md:pl-12">
                <div className="bg-green-100 p-6 rounded-lg w-full max-w-md">
                  <div className="text-center text-lg font-semibold text-green-800 mb-3">Knowledge Base</div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-green-300 rounded-full h-8 w-8 flex items-center justify-center">1</div>
                    <div className="bg-green-200 h-2 flex-grow rounded-full"></div>
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-green-300 rounded-full h-8 w-8 flex items-center justify-center">2</div>
                    <div className="bg-green-200 h-2 flex-grow rounded-full"></div>
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-green-300 rounded-full h-8 w-8 flex items-center justify-center">3</div>
                    <div className="bg-green-200 h-2 flex-grow rounded-full"></div>
                  </div>
                  <button className="bg-green-400 text-white w-full py-2 rounded flex items-center justify-center gap-2">
                    Verified <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Screen 3: Introducing iSPAI */}
        {currentScreen === 3 && (
          <motion.div
            key="screen3"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col p-8 md:p-16 bg-white overflow-y-auto"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return;
              nextScreen();
            }}
          >
          <div className="flex justify-center mb-6">
            <img src={ispaiLogo} alt="iSPAI Logo" className="h-24 w-auto" />
            </div>
              <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 px-4">
              {/* Left Column: Introducing iSPAI */}
              <div className="flex-1 max-w-xl w-full flex flex-col justify-center">
                <h2 className="text-3xl font-bold text-black mb-6">
                  Introducing iSPAI
                </h2>
                <p className="text-lg mb-4 text-gray-700 leading-relaxed">
                  iSPAI is a platform to create, consume, and monetize knowledge for AI - with attribution, alignment, and incentives built in from the start.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  At its core: Giblet is the Orchestration Agent within iSPAI that helps Creators build and mint Knowledge Bases and Solutions to meet demand from Consumers.
                </p>
              </div>
              
              {/* Right Column: Pre-production MVP Guide */}
              <div className="flex-1 max-w-xl w-full flex flex-col justify-center lg:border-l lg:border-gray-200 lg:pl-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
              <div className="mb-3">
                    <Badge variant="secondary" className="text-sm font-semibold">Pre-production MVP</Badge>
                  </div>
                  
                  <div className="space-y-3 text-sm text-gray-700">
                    <div>
                      <p className="font-semibold mb-2">Platform Guide:</p>
                      <ul className="space-y-2 ml-2">
                        <li className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-black flex-shrink-0"></div>
                          <span>Black buttons = Demonstrations / Informational</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-500 flex-shrink-0"></div>
                          <span>Green buttons = Live features</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-700 flex items-center justify-center flex-shrink-0">
                            <FileEdit className="h-3.5 w-3.5 text-white" />
                          </div>
                          <span>Feedback & DMs</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-700 flex items-center justify-center flex-shrink-0">
                            <Bell className="h-3.5 w-3.5 text-white" />
                          </div>
                          <span>Notifications</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-6 h-6 flex-shrink-0 rounded-full overflow-hidden bg-white border-2 border-gray-300">
                            <img src={gibletOwlIcon} alt="Giblet Chat" className="w-full h-full object-cover" />
                          </div>
                          <span>Giblet Chat</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Screen 4: iSPAI Platform Diagram */}
        {currentScreen === 4 && (
          <motion.div
            key="screen4"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col p-8 md:p-16 bg-white overflow-y-auto"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return;
              nextScreen();
            }}
          >
          <div className="flex justify-center mb-6">
            <img src={ispaiLogo} alt="iSPAI Logo" className="h-24 w-auto" />
            </div>
            <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 px-4">
              {/* Left Column: iSPAI Platform Diagram */}
              <div className="flex-1 max-w-xl w-full">
            <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="text-3xl font-bold text-center mb-4">iSPAI Platform</h3>
                  
                  {/* Enterprise bar */}
                  <div className="bg-blue-600 text-white font-bold py-1.5 px-3 rounded-t-md text-center mb-1">
                    Enterprise
                  </div>
                  
                  <div className="flex mb-1 gap-4 text-center">
                    <div className="bg-blue-100 p-3 rounded-md flex-1">
                      <h4 className="font-bold mb-2">AI Assistant</h4>
                      <p className="text-sm">Intelligent processing & curation</p>
                    </div>
                    <div className="text-gray-400 flex items-center justify-center">
                      <span className="text-2xl font-bold">+</span>
                    </div>
                    <div className="bg-green-100 p-3 rounded-md flex-1">
                      <h4 className="font-bold mb-2">Web3 & Smart Contracts</h4>
                      <p className="text-sm">Attribution & compensation</p>
                    </div>
                  </div>
                  
                  {/* Workforce bar */}
                  <div className="bg-green-600 text-white font-bold py-1.5 px-3 text-center mb-4">
                    Workforce
                  </div>
                  
                  <div className="space-y-4">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-4 cursor-help">
                            <div className="bg-purple-100 h-10 w-10 rounded-full flex items-center justify-center font-bold">1</div>
                            <p className="font-medium">Create</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p>Create new or better knowledge bases</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-4 cursor-help">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-[#ccf4fa] font-bold">2</div>
                            <p className="font-medium">Compose</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p>Compose KBs into solutions to enterprise problems</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-4 cursor-help">
                            <div className="bg-green-100 h-10 w-10 rounded-full flex items-center justify-center font-bold">3</div>
                            <p className="font-medium">Consume</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p>Consume solutions by industry for enterprise AI</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
              
              {/* Right Column: Creation Functions */}
              <div className="flex-1 max-w-xl w-full flex flex-col justify-center lg:border-l lg:border-gray-200 lg:pl-8">
            <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="text-xl font-bold mb-3 text-gray-800">Creation Functions to Test</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(a)</span>
                      <span className="text-gray-700">Create mySPAI profile</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(b)</span>
                      <span className="text-gray-700">Create a GibNifty profile image</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(c)</span>
                      <span className="text-gray-700">Invite a colleague</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(d)</span>
                      <span className="text-gray-700">Create knowledge</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(e)</span>
                      <span className="text-gray-700">Launch a sprint</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 font-bold flex-shrink-0 text-lg">(f)</span>
                      <span className="text-gray-700">Connect a KB and chat with Giblet</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Screen 5: The Difference */}
        {currentScreen === 5 && (
          <motion.div
            key="screen5"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col p-6 md:p-12 bg-white"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('.slider-container')) return;
              nextScreen();
            }}
          >
            <div className="flex justify-center mb-6">
              <img src={ispaiLogo} alt="iSPAI Logo" className="h-24 w-auto" />
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-full max-w-4xl mx-auto">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h3 className="text-3xl font-bold text-center mb-4 text-black">The Difference</h3>
                  <p className="text-center text-gray-600 mb-3">Drag to compare</p>
                  <div className="mb-4 slider-container">
                    <Slider value={comparisonValue} onValueChange={setComparisonValue} max={100} step={1} className="cursor-pointer" />
                  </div>
                  <div className="text-sm text-center flex justify-between mb-6">
                    <span className="font-medium">Before</span>
                    <span className="font-medium">After</span>
                  </div>
                  
                  <div className="relative overflow-hidden bg-white rounded-lg border border-gray-100 min-h-[560px]">
                    <div className="absolute top-0 left-0 right-0 bottom-0">
                      {/* Left side (Before) */}
                      <div className="absolute top-0 left-0 right-0 bottom-0" style={{
                        clipPath: `inset(0 ${comparisonValue[0]}% 0 0)`
                      }}>
                        <div className="p-6">
                          <div className="flex flex-col h-full">
                            <div className="mb-4">
                              <Badge variant="destructive" className="mb-3 text-xs px-2 py-0.5">Before</Badge>
                              <h3 className="font-bold text-gray-900 text-xl text-center">Traditional Process</h3>
                            </div>
                            <div className="space-y-2 flex-1">
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Siloed Knowledge Repositories</div>
                              </div>
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Manual Content Management</div>
                              </div>
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Static and Outdated Information</div>
                              </div>
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Limited Search Capabilities</div>
                              </div>
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Low Engagement and Participation</div>
                              </div>
                              <div className="flex items-start gap-3 bg-red-50 p-2 rounded-lg">
                                <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Centralized Control of Knowledge Silos</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right side (After) */}
                      <div className="absolute top-0 left-0 right-0 bottom-0" style={{
                        clipPath: `inset(0 0 0 ${100 - comparisonValue[0]}%)`
                      }}>
                        <div className="p-6">
                          <div className="flex flex-col h-full">
                            <div className="mb-4">
                              <Badge variant="secondary" className="mb-3 text-xs px-2 py-0.5 bg-green-100 text-green-700 border-green-200">After iSPAI</Badge>
                              <h3 className="font-bold text-gray-900 text-xl text-center">Knowledge Ecosystem</h3>
                            </div>
                            <div className="space-y-2 flex-1">
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Integrated Knowledge Ecosystems</div>
                              </div>
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">AI-enabled Knowledge Capture and Retention</div>
                              </div>
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Dynamic Knowledge Updates</div>
                              </div>
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Advanced Search and Discovery</div>
                              </div>
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">High Engagement with Residual Incentives</div>
                              </div>
                              <div className="flex items-start gap-3 bg-green-50 p-2 rounded-lg">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <div className="text-gray-900 font-medium">Decentralized Curation of Wisdom</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Screen 6: Join Waitlist */}
        {currentScreen === 6 && (
          <motion.div
            key="screen6"
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="min-h-screen flex flex-col p-6 md:p-12 bg-white"
          >
            <div className="flex-1 flex flex-col">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold mb-4 text-black">
                  Join the iSPAI Waitlist
                </h2>
                <p className="text-base text-gray-700">
                  Be among the first to experience the iSPAI platform when it launches.
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between flex-1">
            <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
              <div className="flex justify-center mb-4">
                    <img src={ispaiDemoQr} alt="Book a Demo QR Code" className="w-48 h-auto" />
                  </div>
                  
                  <p className="text-base mb-6 text-gray-700 text-center">
                    Want to see the full experience? Get a guided walkthrough from our team.
                  </p>
                </div>
                <div className="md:w-1/2 md:border-l md:border-gray-200 md:pl-8">
                  <div className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex flex-col gap-4">
                      <Button 
                        variant="active" 
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 text-base"
                        onClick={async () => {
                          // Check if coming from invitation explore flow
                          const fromInvitation = sessionStorage.getItem("from_invitation_explore");
                          const invitationData = sessionStorage.getItem("invitation_data");
                          
                          if (fromInvitation && invitationData) {
                            // Parse invitation to get token
                            const invitation = JSON.parse(invitationData);
                            
                            // Track intro completion before navigating
                            try {
                              await supabase.functions.invoke("track-invitation-response", {
                                body: {
                                  tracking_token: invitation.tracking_token,
                                  action_type: "intro_completed",
                                },
                              });
                            } catch (error) {
                              console.error("Error tracking intro completion:", error);
                            }
                            
                            navigate(`/signup-from-invitation?token=${invitation.tracking_token}`);
                          } else if (!user) {
                            navigate('/auth');
                          } else if (profile?.username) {
                            navigate('/creators');
                          } else {
                            navigate('/create-myspai-profile');
                          }
                        }}
                      >
                        {profile?.username ? 'View My Profile' : 'Create a mySPAI Profile'} <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Navigation */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-center">
                  <div className="cursor-pointer" onClick={() => navigate("/")}>
                    <img src="/lovable-uploads/cc9b631c-7ea4-4370-9a8a-b314154af48d.png" alt="iSPAI Logo" className="h-20 object-contain" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Registration Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Join iSPAI Waitlist - {selectedUserType}</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedUserType === "Consumer" ? "Register your organization to be among the first to access iSPAI's knowledge solutions." : "Join our creator community and be the first to start building knowledge assets with iSPAI."}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRegister} className="space-y-3 mt-3">
            {selectedUserType === "Consumer" ? <>
                <div className="space-y-1">
                  <Label htmlFor="companyName" className="text-sm">Company or Organization Name *</Label>
                  <Input id="companyName" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Enter company/organization name" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="individualName" className="text-sm">Individual Name *</Label>
                  <Input id="individualName" value={individualName} onChange={e => setIndividualName(e.target.value)} placeholder="Enter your full name" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="individualRole" className="text-sm">Role *</Label>
                  <Input id="individualRole" value={individualRole} onChange={e => setIndividualRole(e.target.value)} placeholder="Enter your role/title" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="industry" className="text-sm">Industry *</Label>
                  <Input id="industry" value={industry} onChange={e => setIndustry(e.target.value)} placeholder="Enter your industry" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-sm">Email *</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required className="text-sm" />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox id="scheduleBriefing" checked={scheduleBriefing} onCheckedChange={checked => setScheduleBriefing(checked === true)} />
                  <Label htmlFor="scheduleBriefing" className="text-sm">Schedule a Briefing with iSPAI</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox id="authorizeWaitlist" checked={authorizeWaitlist} onCheckedChange={checked => setAuthorizeWaitlist(checked === true)} />
                  <Label htmlFor="authorizeWaitlist" className="text-sm">Authorize including company name on iSPAI waitlist</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="includeNameOnWaitlist" checked={includeNameOnWaitlist} onCheckedChange={checked => setIncludeNameOnWaitlist(checked === true)} />
                  <Label htmlFor="includeNameOnWaitlist" className="text-sm">Include my name on the waitlist</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="inviteFriend" checked={inviteFriend} onCheckedChange={checked => setInviteFriend(checked === true)} />
                  <Label htmlFor="inviteFriend" className="text-sm">Invite a friend or colleague to this introduction</Label>
                </div>

                {inviteFriend && <div className="space-y-2 pl-6 border-l-2 border-gray-200">
                    <div className="space-y-1">
                      <Label htmlFor="friendName" className="text-sm">Friend's Name</Label>
                      <Input id="friendName" value={friendName} onChange={e => setFriendName(e.target.value)} placeholder="Enter friend's name" className="text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="friendEmail" className="text-sm">Friend's Email</Label>
                      <Input id="friendEmail" type="email" value={friendEmail} onChange={e => setFriendEmail(e.target.value)} placeholder="friend@example.com" className="text-sm" />
                    </div>
                  </div>}
                
                <div className="space-y-1">
                  <Label htmlFor="comments" className="text-sm">Comments</Label>
                  <textarea id="comments" value={comments} onChange={e => setComments(e.target.value)} placeholder="Any additional information or questions..." className="w-full p-2 border border-gray-300 rounded-md text-sm" rows={2} />
                </div>
              </> : <>
                <div className="space-y-1">
                  <Label htmlFor="creatorName" className="text-sm">Full Name *</Label>
                  <Input id="creatorName" value={creatorName} onChange={e => setCreatorName(e.target.value)} placeholder="Enter your full name" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="creatorEmail" className="text-sm">Email *</Label>
                  <Input id="creatorEmail" type="email" value={creatorEmail} onChange={e => setCreatorEmail(e.target.value)} placeholder="you@example.com" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="creatorExpertise" className="text-sm">Area of Expertise *</Label>
                  <Input id="creatorExpertise" value={creatorExpertise} onChange={e => setCreatorExpertise(e.target.value)} placeholder="e.g., Construction, Engineering, Project Management" required className="text-sm" />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="creatorExperience" className="text-sm">Years of Experience *</Label>
                  <Input id="creatorExperience" value={creatorExperience} onChange={e => setCreatorExperience(e.target.value)} placeholder="Years in your field" required className="text-sm" />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="creatorIncludeNameOnWaitlist" checked={creatorIncludeNameOnWaitlist} onCheckedChange={checked => setCreatorIncludeNameOnWaitlist(checked === true)} />
                  <Label htmlFor="creatorIncludeNameOnWaitlist" className="text-sm">Include my name on the waitlist</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="creatorInviteFriend" checked={creatorInviteFriend} onCheckedChange={checked => setCreatorInviteFriend(checked === true)} />
                  <Label htmlFor="creatorInviteFriend" className="text-sm">Invite a friend or colleague to this introduction</Label>
                </div>

                {creatorInviteFriend && <div className="space-y-2 pl-6 border-l-2 border-gray-200">
                    <div className="space-y-1">
                      <Label htmlFor="creatorFriendName" className="text-sm">Friend's Name</Label>
                      <Input id="creatorFriendName" value={creatorFriendName} onChange={e => setCreatorFriendName(e.target.value)} placeholder="Enter friend's name" className="text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="creatorFriendEmail" className="text-sm">Friend's Email</Label>
                      <Input id="creatorFriendEmail" type="email" value={creatorFriendEmail} onChange={e => setCreatorFriendEmail(e.target.value)} placeholder="friend@example.com" className="text-sm" />
                    </div>
                  </div>}
                
                <div className="space-y-1">
                  <Label htmlFor="creatorComments" className="text-sm">Tell us about your expertise</Label>
                  <textarea id="creatorComments" value={creatorComments} onChange={e => setCreatorComments(e.target.value)} placeholder="What knowledge would you like to share? What problems do you solve?" className="w-full p-2 border border-gray-300 rounded-md text-sm" rows={2} />
                </div>
              </>}

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setRegisterDialogOpen(false)} className="text-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="text-sm">
                {isSubmitting ? "Registering..." : "Join Waitlist"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Profile Dialog */}
      <Dialog open={showCreateProfile} onOpenChange={setShowCreateProfile}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Welcome to iSPAI!</DialogTitle>
            <DialogDescription className="text-sm">
              You've successfully joined our waitlist. Would you like to create your mySPAI profile now?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Creating a mySPAI profile will help you get the most out of the platform when it launches and start building your individual knowledge network.
            </p>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateProfile(false)} className="text-sm">
                Maybe Later
              </Button>
              <Button onClick={handleCreateProfile} className="text-sm">
                Create mySPAI Profile
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <GibletChatDialogEnhanced
        open={gibletChatOpen}
        onOpenChange={setGibletChatOpen}
      />
    </div>;
};
export default Intro;